import { useCallback, useEffect, useRef, useState } from 'react';
import { Monitor, Smartphone } from 'lucide-react';
import { vorschauHtml } from './vorschauHtml';
import './MailVorschau.css';

type Breite = 'computer' | 'handy';

type Props = {
  html: string;
  titel?: string;
  /** Startbreite; der Schalter oben wechselt. */
  start?: Breite;
  /** Höhe begrenzen (mit eigenem Scrollen), z. B. neben einem Formular. */
  maxHoehe?: number;
  tour?: string;
};

/**
 * Die Mail, wie sie ankommt — in einem abgeschotteten Rahmen: Skripte laufen
 * darin nicht, Links öffnen sich in einem neuen Tab (zum Prüfen).
 */
export function MailVorschau({ html, titel = 'Vorschau der Mail', start = 'computer', maxHoehe, tour }: Props) {
  const [breite, setBreite] = useState<Breite>(start);
  const [hoehe, setHoehe] = useState(600);
  const rahmen = useRef<HTMLIFrameElement>(null);

  const messen = useCallback(() => {
    const doc = rahmen.current?.contentDocument;
    if (!doc?.documentElement) return;
    setHoehe(Math.max(300, doc.documentElement.scrollHeight));
  }, []);

  useEffect(() => {
    const t = setTimeout(messen, 60);
    return () => clearTimeout(t);
  }, [breite, messen]);

  return (
    <div className="mv" data-tour={tour}>
      <div className="mv-kopf">
        <span className="mv-titel">{titel}</span>
        <div className="seg-control seg-control--sm" role="group" aria-label="Breite der Vorschau">
          <button type="button" className={`seg-btn${breite === 'computer' ? ' active' : ''}`} aria-pressed={breite === 'computer'} onClick={() => setBreite('computer')}>
            <Monitor size={13} aria-hidden="true" /> Computer
          </button>
          <button type="button" className={`seg-btn${breite === 'handy' ? ' active' : ''}`} aria-pressed={breite === 'handy'} onClick={() => setBreite('handy')}>
            <Smartphone size={13} aria-hidden="true" /> Handy
          </button>
        </div>
      </div>
      <div className="mv-buehne" style={maxHoehe ? { maxHeight: maxHoehe, overflowY: 'auto' } : undefined}>
        <iframe
          ref={rahmen}
          title={titel}
          className={`mv-rahmen mv-rahmen--${breite}`}
          // Ohne allow-scripts läuft im Rahmen nichts; allow-same-origin nur,
          // damit die Seite die Höhe messen kann.
          sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          srcDoc={vorschauHtml(html)}
          onLoad={messen}
          style={{ height: hoehe }}
        />
      </div>
    </div>
  );
}
